import React, { useEffect, useRef, useState } from 'react';
import config from 'init/config';
import SiteSearchAPIConnector from '@elastic/search-ui-site-search-connector';
import { SearchProvider, WithSearch } from '@elastic/react-search-ui';
import styled from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';
import GradientLinearProgress from 'components/GradientLinearProgress';

import IconButton from '@material-ui/core/IconButton';
import ArrowLeftIcon from 'mdi-material-ui/ArrowLeft';
import ArrowRightIcon from 'mdi-material-ui/ArrowRight';

import PublicationResultCard from 'views/Publications/elements/PublicationResultCard';

const connector = new SiteSearchAPIConnector({
  documentType: 'page',
  engineKey: config.swiftypeConfig.engine_key
});

const ProgressWrapper = styled.div`
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: center;
`;

const HorizontalWrapper = styled.div`
  display: flex;
  gap: 18px;
  padding: 12px;
  margin: -12px;
  padding-left: ${props => (props.padded ? '44px' : '12px')};

  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  > a {
    @media (min-width: 960px) {
      width: calc(33.33% - 12px);
    }
    width: 80vw;
    flex-shrink: 0;
    max-width: 372px;
  }
`;

const ScrollControls = styled.div`
  display: flex;
  align-items: center;
  ${GradientLinearProgress} {
    flex-grow: 1;
  }
`;

const RelatedPublications = ({
  researchArea,
  application,
  product,
  padded,
  showControls
}) => {
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState({
    position: 'start',
    xPercent: 0
  });

  const handleArrowClick = direction => {
    const scrollToPosition =
      direction === 'left'
        ? scrollRef.current.scrollLeft - scrollRef.current?.offsetWidth + 6
        : scrollRef.current.scrollLeft + scrollRef.current?.offsetWidth - 6;
    scrollRef.current.scroll({
      left: scrollToPosition,
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const xPercent =
        (scrollRef.current.scrollLeft /
          (scrollRef.current?.scrollWidth -
            scrollRef.current?.offsetWidth -
            1)) *
        100;

      if (scrollRef.current.scrollLeft === 0) {
        setScrollPosition({ position: 'start', xPercent: xPercent });
      } else if (
        scrollRef.current.scrollLeft >=
        scrollRef.current.scrollWidth - scrollRef.current.offsetWidth - 1
      ) {
        setScrollPosition({
          position: 'end',
          xPercent: xPercent
        });
      } else {
        setScrollPosition({
          position: 'middle',
          xPercent: xPercent
        });
      }
    };
    let listenerRefValue = null;
    if (scrollRef.current) {
      scrollRef.current.addEventListener('scroll', handleScroll, {
        passive: true
      });
      listenerRefValue = scrollRef.current;
    }
    return () => {
      listenerRefValue.removeEventListener('scroll', handleScroll);
    };
  }, [scrollRef]);

  return (
    <>
      <HorizontalWrapper
        data-swiftype-index="false"
        padded={padded}
        ref={scrollRef}
      >
        <SearchProvider
          config={{
            apiConnector: connector,
            resultsPerPage: 20,
            trackUrlState: false,
            initialState: {
              filters: [
                {
                  field: 'content_type',
                  values: ['Publication'],
                  type: 'all'
                },
                {
                  field: 'research_area',
                  values: (researchArea || []).map(v => v.title),
                  type: 'any'
                },
                {
                  field: 'application',
                  values: (application || []).map(v => v.title),
                  type: 'any'
                },
                {
                  field: 'product',
                  values: (product || []).map(v => v.title),
                  type: 'any'
                }
              ],
              sortField: 'product_family',
              sortDirection: 'desc'
            },

            alwaysSearchOnInitialLoad: true
          }}
        >
          <WithSearch
            mapContextToProps={({ isLoading, totalResults, results }) => ({
              isLoading,
              totalResults,
              results
            })}
          >
            {({ isLoading, totalResults, results }) => {
              return isLoading ? (
                <ProgressWrapper>
                  <CircularProgress />
                </ProgressWrapper>
              ) : (
                <>
                  {results.map((result, idx) => (
                    <PublicationResultCard
                      size="small"
                      key={idx}
                      loadProductTaxonomy={idx === 0}
                      {...result}
                    />
                  ))}
                </>
              );
            }}
          </WithSearch>
        </SearchProvider>
      </HorizontalWrapper>
      {showControls && (
        <ScrollControls>
          <GradientLinearProgress
            variant="determinate"
            value={scrollPosition.xPercent}
          />
          <IconButton
            aria-label="Scroll Left"
            onClick={() => handleArrowClick('left')}
            variant="text"
            disabled={scrollPosition.position === 'start'}
          >
            <ArrowLeftIcon />
          </IconButton>
          <IconButton
            aria-label="Scroll Right"
            onClick={() => handleArrowClick('right')}
            variant="text"
            disabled={scrollPosition.position === 'end'}
          >
            <ArrowRightIcon />
          </IconButton>
        </ScrollControls>
      )}
    </>
  );
};

export default RelatedPublications;
